import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

/**
 * Calculates the relative luminance of a hex color.
 * Uses the WCAG formula for relative luminance.
 * @param hexColor - A hex color string (e.g., "#ff5733" or "ff5733")
 * @returns The relative luminance value between 0 (black) and 1 (white)
 */
export function getLuminance(hexColor: string): number {
	// Remove # if present
	const hex = hexColor.replace(/^#/, "");

	// Parse RGB values
	const r = Number.parseInt(hex.slice(0, 2), 16) / 255;
	const g = Number.parseInt(hex.slice(2, 4), 16) / 255;
	const b = Number.parseInt(hex.slice(4, 6), 16) / 255;

	// Apply gamma correction
	const gammaCorrect = (value: number) =>
		value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;

	const rLinear = gammaCorrect(r);
	const gLinear = gammaCorrect(g);
	const bLinear = gammaCorrect(b);

	// Calculate luminance using WCAG coefficients
	return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
}

/**
 * Returns an appropriate contrasting text color (black or white) for a given background color.
 * Uses WCAG luminance calculation to determine optimal contrast.
 * @param backgroundColor - A hex color string for the background (e.g., "#ff5733")
 * @param lightColor - The color to use for light text (default: "#ffffff")
 * @param darkColor - The color to use for dark text (default: "#1f2937")
 * @returns The contrasting color string
 */
export function getContrastingTextColor(
	backgroundColor: string,
	lightColor = "#ffffff",
	darkColor = "#1f2937",
): string {
	const luminance = getLuminance(backgroundColor);
	// Use a threshold of 0.179 (WCAG recommended) to determine if background is "light" or "dark"
	return luminance > 0.179 ? darkColor : lightColor;
}

/**
 * Parse a localized currency/number input string into a number.
 * Supports common patterns for pt-BR ("1.234,56") and en-US ("1,234.56").
 * @param value - The string value to parse
 * @param _currency - The currency code (e.g., "BRL", "USD") - not used in parsing but kept for API consistency with formatCurrency
 * @param locale - The locale to use for parsing (defaults to "pt-BR")
 * @returns The parsed number value
 */
export const parseCurrencyInput = (
	value: string,
	_currency: string,
	locale = "pt-BR",
): number => {
	const trimmed = value.trim();
	if (trimmed === "") return Number.NaN;

	// For Portuguese locales, treat "." as thousands separator and "," as decimal
	if (locale.startsWith("pt")) {
		const normalized = trimmed.replace(/\./g, "").replace(",", ".");
		return Number.parseFloat(normalized);
	}

	// Default: treat "," as thousands separator and "." as decimal
	const normalized = trimmed.replace(/,/g, "");
	return Number.parseFloat(normalized);
};
